import React, { useEffect, useMemo } from 'react';
import { BsBoxArrowUpRight, BsFileEarmarkPdf, BsInfoCircle, BsStars } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import { Box, Button, Flex, HStack, Icon, Link, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { createErrorToast } from '../../../../../../../../../../../../../../../redux/modules/app';
import { hideModal, showModal } from '../../../../../../../../../../../../../../../redux/modules/modal';
import { useExtractDocumentExpirations } from '../../../../../../../../../../../../../../../services/gpt-request-service';
import recordKeys from '../../../../../../../../../../../../../../../services/models/record/keys';
import colors from '../../../../../../../../../../../../../../../styles/theme/foundation/colors';
import { queryClient } from '../../../../../../../../../../../../../../../utils/queryClientUtil';

import ExtractionFailedModal from './ExtractionFailedModal';
import ReviewExtractedDatesModal from './ReviewExtractedDatesModal';
import ScanningProgressBarModal from './ScanningProgressBarModal';

const RequestToScanModal = ({
  file,
  onClose,
  document,
  recordId
}) => {
  const dispatch = useDispatch();

  const fileUrl = useMemo(() => URL.createObjectURL(file), [file]);

  useEffect(() => () => URL.revokeObjectURL(fileUrl), [fileUrl]);

  const { mutateAsync: extractDocumentExpirations, isLoading: isRequesting } = useExtractDocumentExpirations();

  const invalidateDocumentQueries = () => {
    queryClient.invalidateQueries(recordKeys.documentExpirations(document.id));
    queryClient.invalidateQueries(recordKeys.recordDocuments(recordId));
    queryClient.invalidateQueries(recordKeys.recordDocumentRequirements(recordId));
  };

  const showReviewModal = gptRequestId => {
    dispatch(hideModal());
    dispatch(showModal(
      () => (
        <ReviewExtractedDatesModal
          documentId={document.id}
          file={file}
          gptRequestId={gptRequestId}
          insuredId={document.insurableId}
          recordId={recordId}
        />
      ),
      { size: '2xl', center: false }
    ));
  };

  const handleUploadWithoutDate = () => {
    dispatch(hideModal());
    invalidateDocumentQueries();
  };

  const handleScanFailed = () => {
    dispatch(hideModal());
    dispatch(showModal(
      () => (
        <ExtractionFailedModal
          file={file}
          onEnterManually={() => showReviewModal(null)}
          onTryAgain={() => handleExtract()}
          onUploadWithoutDate={handleUploadWithoutDate}
        />
      ),
      { size: '2xl', center: false }
    ));
  };

  const handleScanSucceeded = gptRequest => showReviewModal(gptRequest.id);

  async function handleExtract() {
    let gptRequest;

    try {
      gptRequest = await extractDocumentExpirations({ documentIds: [document.id] });
    } catch (error) {
      dispatch(createErrorToast({
        title: 'Could not start the expiration date scan',
        description: error.message,
        duration: 10000
      }));

      return;
    }

    dispatch(hideModal());
    dispatch(showModal(
      () => (
        <ScanningProgressBarModal
          gptRequestId={gptRequest.id}
          onClose={() => {}}
          onFail={handleScanFailed}
          onSuccess={handleScanSucceeded}
        />
      ),
      { size: 'xl' }
    ));
  }

  return (
    <Box textAlign="left">
      <Text
        color={colors.greyScale['900']}
        fontSize="18px"
        fontWeight="semibold"
        lineHeight="26px"
        mb={2}
      >
        Would you like to extract expiration dates from this document?
      </Text>

      <Text color={colors.greyScale['700']} fontSize="15px" lineHeight="24px">
        We&apos;ll scan
        {' '}
        <Link
          isExternal
          alignItems="center"
          color={colors.primaryScale['800']}
          display="inline-flex"
          fontWeight="semibold"
          gap={1}
          href={fileUrl}
          verticalAlign="baseline"
        >
          <Icon as={BsFileEarmarkPdf} boxSize="16px" />
          {file.name}
          <Icon as={BsBoxArrowUpRight} boxSize="12px" />
        </Link>
        {' '}
        for each policy and its expiration date. You&apos;ll review what we find before it&apos;s saved.
      </Text>

      <Flex
        alignItems="flex-start"
        bg="#FDF3D8"
        borderRadius="lg"
        color="#7A5C1E"
        fontSize="14px"
        gap={3}
        lineHeight="20px"
        mt={5}
        px={4}
        py={4}
      >
        <Icon as={BsInfoCircle} boxSize="16px" flexShrink={0} mt="2px" />
        <Text>Skip and the document uploads without a date. You can add one later.</Text>
      </Flex>

      <HStack justifyContent="space-between" mt={6} spacing={3}>
        <Button
          flex={1}
          size="lg"
          variant="secondary"
          onClick={onClose}
        >
          Skip, Upload Without Date
        </Button>

        <Button
          flex={1}
          isLoading={isRequesting}
          leftIcon={<Icon as={BsStars} boxSize="16px" />}
          size="lg"
          variant="primary"
          onClick={() => handleExtract()}
        >
          Yes, Extract Date
        </Button>
      </HStack>
    </Box>
  );
};

RequestToScanModal.propTypes = {
  file: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  document: PropTypes.object.isRequired,
  recordId: PropTypes.number.isRequired
};

export default RequestToScanModal;
